import LoadingButton from '@/common/components/base/button/loadingButton';
import DateInput from '@/common/components/base/input/dateInput/DateInput';
import TextInputWithSelectOnMap from '@/common/components/base/input/mapInput/TextInputWithSelectOnMap';
import TimeInput from '@/common/components/base/input/timeInput/TimeInput';
import NumberInputWithWords from '@/common/components/base/numberInputWithWords/NumberInputWithWords';
import PercentageInput from '@/common/components/base/percentageInput/PercentageInput';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import MainLayout from '@/common/components/layouts/MainLayout';
import { convertPersianToGregorian } from '@/common/utils/convertPersianToGregorian';
import { requiredText } from '@/common/utils/rules/publicRules';
import { validateNotPastDate } from '@/modules/adjusterReportModules/IncidentDetails/utils/incidentDetails.util';
import { VisitInformationForm } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getVisitInformation/getVisitInformationQuer.type';
import getVisitInformation from '@/modules/adjusterReportModules/IncidentParticipants/queries/getVisitInformation/getVisitInformationQueryOption';
import { patchVisitInformationQueryPayload } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchVisitInformation/patchVisitInformationQuery.type';
import { usePatchVisitInformationMutation } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchVisitInformation/patchVisitInformationQueryOption';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Flex } from 'styled-system/jsx';

export default () => {
  return (
    <MainLayout title="بازدید اولیه / کارشناسی" header="titleHeader">
      <InitialVisitVehicleVisitInformation />
    </MainLayout>
  );
};
const InitialVisitVehicleVisitInformation = () => {
  const { id, damagedId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate: patchVisitformation, isPending: loading } =
    usePatchVisitInformationMutation();
  const { data } = useQuery(
    getVisitInformation({
      damaged_id: damagedId!,
      id: id!,
      source: 'vehicle',
      type: 'initial',
    }),
  );

  const methods = useForm<VisitInformationForm>({
    mode: 'onChange',
  });
  const onSubmit = (formData: VisitInformationForm) => {
    const formattedData: patchVisitInformationQueryPayload = {
      visit_location: formData?.visit_location,
      visit_date: convertPersianToGregorian(
        formData?.visit_date?.toString() ?? '',
      ),
      visit_site: formData.visit_site?.value,
      distance: !!formData?.distance ? formData?.distance : 0,
      parts_repair_length: formData?.parts_repair_length,
      visit_number: formData?.visit_number,
      visit_time: formData?.visit_time,
      location_phi: formData?.visit_location_phi ?? null,
      location_lambda: formData?.visit_location_lambda ?? null,
    };
    patchVisitformation(
      {
        damaged_id: damagedId!,
        id: id!,
        data: formattedData,
        type: 'initial',
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getVisitInformation({
              damaged_id: damagedId!,
              id: id!,
              source: 'vehicle',
              type: 'initial',
            }).queryKey,
          });
          navigate(-1);
        },
      },
    );
  };
  useEffect(() => {
    if (data) {
      methods.reset({
        ...data,
        visit_site: {
          value: data.visit_site?.value,
          label: data.visit_site?.label,
        },
        visit_location: data.visit_location,
        visit_location_phi: data.visit_location_phi ?? null,
        visit_location_lambda: data.visit_location_lambda ?? null,
      });
      if (data?.visit_date)
        setTimeout(() => {
          methods.trigger('visit_date');
        });
    }
  }, [data, methods]);

  return (
    <Box width="100%" pt={'24px'} position="relative">
      <TitleDescription>اطلاعات بازدید</TitleDescription>
      <FormProvider {...methods}>
        <Flex direction="column" gap={8} mt={24}>
          <Box>
            <DateInput
              label="تاریخ بازدید"
              name="visit_date"
              buttonText="انتخاب تاریخ"
              modalLabel="انتخاب تاریخ تولد"
              rules={{ required: requiredText, validate: validateNotPastDate }}
              minDate={new Date(Date.now() - 24 * 60 * 60 * 1000)}
            />
          </Box>
          <Box>
            <TimeInput
              buttonText="انتخاب زمان"
              modalLabel="انتخاب زمان بازدید"
              name="visit_time"
              label="ساعت بازدید"
              rules={{ required: requiredText }}
            />
          </Box>
          <Box>
            <PercentageInput
              name="visit_number"
              label="تعداد بازدید"
              percentage={1}
              maxPercentage={1000}
              placeHolder="تعداد بازدید را وارد کنید"
            />
          </Box>
          <Box>
            <NumberInputWithWords
              label="مسافت طی شده (کیلومتر )"
              name="distance"
              placeholder="مسافت طی شده را وارد کنید"
              showPriceToWords={false}
              hasSeparator={false}
            />
          </Box>
          <Box>
            <TextInputWithSelectOnMap
              rules={{ required: requiredText }}
              label="آدرس محل بازدید"
              entranceModalText={
                'در ادامه آدرس محل وقوع حادثه را بر روی نقشه مشخص کنید .'
              }
              mapModalTitle="محل بازدید"
              name="visit_location"
              phiName="visit_location_phi"
              lambdaName="visit_location_lambda"
            />
          </Box>
          <Box pb={24}>
            <LoadingButton
              onClick={methods.handleSubmit(onSubmit)}
              loading={loading}
              fullWidth
              disabled={!methods?.formState?.isValid}
            >
              ثبت
            </LoadingButton>
          </Box>
        </Flex>
      </FormProvider>
    </Box>
  );
};
